import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../components/ui/dropdown-menu';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import {
  ArrowUpCircle,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  Eye,
  RefreshCw,
  Copy,
  ShieldCheck,
  Wallet,
  IndianRupee,
  Coins,
  Plus,
  Power,
  Download,
  Zap,
  ExternalLink,
  Search,
  MoreVertical,
} from 'lucide-react';

const STATUS_CONFIG = {
  created: { color: 'bg-gray-100 text-gray-700', icon: Clock, label: 'Created' },
  proof_submitted: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'Proof Submitted' },
  pending_verification: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'Pending Verification' },
  under_review: { color: 'bg-blue-100 text-blue-700', icon: Clock, label: 'Under Review' },
  verified: { color: 'bg-blue-100 text-blue-700', icon: ShieldCheck, label: 'Verified' },
  approved: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Approved' },
  credited: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Credited' },
  rejected: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Rejected' },
  expired: { color: 'bg-gray-100 text-gray-500', icon: XCircle, label: 'Expired' },
};

const CHAIN_CONFIG = {
  not_checked: { color: 'bg-gray-100 text-gray-600', dot: '○', label: 'Not Verified' },
  checking: { color: 'bg-blue-100 text-blue-700', dot: '◐', label: 'Checking' },
  verified: { color: 'bg-green-100 text-green-700', dot: '✓', label: 'On-Chain Verified' },
  failed: { color: 'bg-red-100 text-red-700', dot: '✕', label: 'Verification Failed' },
};

const ChainBadge = ({ status }) => {
  const c = CHAIN_CONFIG[status] || CHAIN_CONFIG.not_checked;
  return <Badge className={`${c.color} border-none`} data-testid={`chain-badge-${status || 'not_checked'}`}><span className="mr-1">{c.dot}</span>{c.label}</Badge>;
};

const _normUi = (s) => {
  if (s === 'credited') return 'Credited';
  if (s === 'rejected') return 'Rejected';
  if (s === 'expired') return 'Expired';
  if (s === 'approved') return 'Approved';
  return 'Pending Approval';
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(amount || 0);

const formatDate = (d) =>
  !d ? '-' : new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const StatusBadge = ({ status }) => {
  const c = STATUS_CONFIG[status] || STATUS_CONFIG.created;
  const Icon = c.icon;
  return (
    <Badge className={`${c.color} border-none`}>
      <Icon className="w-3 h-3 mr-1" />
      {c.label}
    </Badge>
  );
};

export default function AdminFundingPage() {
  const { api } = useAuth();

  const [requests, setRequests] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [overview, setOverview] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [fSearch, setFSearch] = useState('');
  const [fStatus, setFStatus] = useState('all');
  const [fChain, setFChain] = useState('all');
  const [onchain, setOnchain] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Wallet management form
  const [newAddress, setNewAddress] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [addrValid, setAddrValid] = useState(null);
  const [savingWallet, setSavingWallet] = useState(false);

  // Settings form
  const [settingsForm, setSettingsForm] = useState({ enabled: false, exchange_rate: '', min_amount: '', max_amount: '', instructions: '' });
  const [savingSettings, setSavingSettings] = useState(false);

  // Funding method availability flags
  const [methodFlags, setMethodFlags] = useState({ crypto_deposit_enabled: true, cash_deposit_enabled: false, bank_transfer_enabled: false });
  const [savingMethods, setSavingMethods] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [allRes, pendingRes, overviewRes, walletsRes, settingsRes, methodsRes] = await Promise.all([
        api.get('/funding/admin/requests?limit=50'),
        api.get('/funding/admin/requests/pending'),
        api.get('/funding/admin/overview'),
        api.get('/funding/admin/usdt/wallets'),
        api.get('/funding/admin/settings/usdt'),
        api.get('/funding/admin/methods'),
      ]);
      setRequests(allRes.data.requests || []);
      setPendingRequests(pendingRes.data.requests || []);
      setOverview(overviewRes.data);
      setWallets(walletsRes.data.wallets || []);
      setMethodFlags(methodsRes.data || { crypto_deposit_enabled: true, cash_deposit_enabled: false, bank_transfer_enabled: false });
      const s = settingsRes.data || {};
      setSettings(s);
      setSettingsForm({
        enabled: !!s.enabled,
        exchange_rate: s.exchange_rate ?? '',
        min_amount: s.min_amount ?? '',
        max_amount: s.max_amount ?? '',
        instructions: s.instructions ?? '',
      });
    } catch (error) {
      console.error('Failed to fetch funding data:', error);
      toast.error('Failed to load funding data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 25000);  // live refresh of pending funding queue
    return () => clearInterval(id);
  }, [fetchData]);

  const handleOnchainVerify = async (fundingId) => {
    setVerifying(true);
    setOnchain(null);
    try {
      const res = await api.post(`/funding/admin/${fundingId}/onchain-verify`);
      setOnchain(res.data);
      if (res.data.verified) toast.success('Transaction verified on-chain');
      else toast.error(res.data.failure_reason || 'On-chain verification failed');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verification request failed');
    } finally {
      setVerifying(false);
    }
  };

  const handleApproveCredit = async (fundingId) => {
    setProcessing(true);
    try {
      await api.post(`/funding/admin/${fundingId}/approve`);
      toast.success('Funding approved — merchant wallet credited');
      setShowDetailsDialog(false);
      setShowApproveDialog(false);
      setOnchain(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to credit wallet');
    } finally {
      setProcessing(false);
    }
  };

  // Open the approval confirmation modal for a request
  const openApprove = (r) => { setSelectedRequest(r); setShowApproveDialog(true); };

  const handleApprove = async (fundingId) => {
    setProcessing(true);
    try {
      await api.post(`/funding/admin/${fundingId}/approve`);
      toast.success('Funding approved — merchant wallet credited');
      await fetchData();
      setShowApproveDialog(false);
      setShowDetailsDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to credit wallet');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { toast.error('Please enter a rejection reason'); return; }
    setProcessing(true);
    try {
      await api.post(`/funding/admin/${selectedRequest.funding_id}/reject?reason=${encodeURIComponent(rejectReason)}`);
      toast.success('Funding request rejected');
      setShowRejectDialog(false);
      setShowDetailsDialog(false);
      setRejectReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessing(false);
    }
  };

  const validateAddress = async () => {
    if (!newAddress.trim()) return;
    try {
      const res = await api.get(`/funding/admin/usdt/validate-address?address=${encodeURIComponent(newAddress.trim())}`);
      setAddrValid(res.data.valid);
      toast[res.data.valid ? 'success' : 'error'](res.data.valid ? 'Valid TRC20 address' : 'Invalid TRC20 address');
    } catch {
      setAddrValid(false);
    }
  };

  const addWallet = async (setActive) => {
    if (!newAddress.trim()) { toast.error('Enter a wallet address'); return; }
    setSavingWallet(true);
    try {
      await api.post('/funding/admin/usdt/wallets', { address: newAddress.trim(), label: newLabel.trim(), set_active: setActive });
      toast.success(setActive ? 'Wallet added & activated' : 'Wallet added');
      setNewAddress(''); setNewLabel(''); setAddrValid(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add wallet');
    } finally {
      setSavingWallet(false);
    }
  };

  const activateWallet = async (id) => {
    try { await api.post(`/funding/admin/usdt/wallets/${id}/activate`); toast.success('Wallet activated'); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const disableWallet = async (id) => {
    try { await api.post(`/funding/admin/usdt/wallets/${id}/disable`); toast.success('Wallet disabled'); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const saveMethods = async (next) => {
    setSavingMethods(true);
    const prev = methodFlags;
    setMethodFlags(next);
    try {
      await api.put('/funding/admin/methods', next);
      toast.success('Funding methods updated');
    } catch (error) {
      setMethodFlags(prev);
      toast.error(error.response?.data?.detail || 'Failed to update funding methods');
    } finally {
      setSavingMethods(false);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put('/funding/admin/settings/usdt', {
        enabled: settingsForm.enabled,
        exchange_rate: parseFloat(settingsForm.exchange_rate) || 0,
        min_amount: parseFloat(settingsForm.min_amount) || 0,
        max_amount: parseFloat(settingsForm.max_amount) || 0,
        instructions: settingsForm.instructions,
        network: 'TRC20',
      });
      toast.success('Funding settings saved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success('Copied'); };

  const downloadQR = (address) => {
    const svg = document.getElementById(`qr-${address}`);
    if (!svg) return;
    const data = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([data], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `usdt-trc20-${address.slice(0, 8)}.svg`; a.click();
    URL.revokeObjectURL(url);
  };

  const activeWallet = wallets.find((w) => w.status === 'active');

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const kpis = overview ? [
    { label: 'Total USDT Received', value: `${overview.usdt.total_usdt_received} USDT`, sub: formatCurrency(overview.usdt.total_inr_credited) + ' credited', icon: Coins, accent: 'text-emerald-500' },
    { label: "Today's Funding", value: `${overview.usdt.today_usdt} USDT`, sub: formatCurrency(overview.usdt.today_inr), icon: IndianRupee, accent: 'text-blue-500' },
    { label: 'Pending Review', value: overview.funding_counts.pending, sub: formatCurrency(overview.funding_counts.pending_inr) + ' pending', icon: Clock, accent: 'text-yellow-500' },
    { label: 'Total Wallet Balance', value: formatCurrency(overview.wallet.total_available_balance), sub: formatCurrency(overview.wallet.total_pending_balance) + ' pending', icon: Wallet, accent: 'text-primary' },
  ] : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold">USDT Funding</h1>
          <p className="text-muted-foreground">Manage TRC20 receiving wallets, verify deposits and credit merchant wallets</p>
        </div>
        <Button variant="outline" onClick={fetchData} data-testid="funding-refresh-btn">
          <RefreshCw className="mr-2 h-4 w-4" /> Refresh
        </Button>
      </div>

      {/* KPI cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {kpis.map((k) => (
          <Card key={k.label} className="relative overflow-hidden">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">{k.label}</p>
                  <p className={`text-2xl font-bold font-mono tabular-nums ${k.accent}`}>{k.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{k.sub}</p>
                </div>
                <k.icon className={`w-8 h-8 opacity-40 ${k.accent}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="requests" className="w-full">
        <TabsList>
          <TabsTrigger value="requests" data-testid="tab-requests">Funding Requests</TabsTrigger>
          <TabsTrigger value="wallet" data-testid="tab-wallet">USDT Wallet & Settings</TabsTrigger>
        </TabsList>

        {/* ===== REQUESTS TAB ===== */}
        <TabsContent value="requests" className="space-y-6">
          {pendingRequests.length > 0 && (() => {
            const q = fSearch.trim().toLowerCase();
            const rows = pendingRequests.filter((r) => {
              const chain = r.chain_verification_status || r.onchain_status || 'not_checked';
              if (fChain !== 'all' && chain !== fChain) return false;
              if (fStatus !== 'all' && (r.funding_status || 'pending_approval') !== fStatus) return false;
              if (!q) return true;
              return [r.funding_id, r.merchant?.business_name, r.merchant_email || r.merchant?.email, r.mid || r.merchant?.mid]
                .some((v) => (v || '').toString().toLowerCase().includes(q));
            });
            const allSel = rows.length > 0 && rows.every((r) => selectedIds.has(r.funding_id));
            const toggleAll = () => {
              const next = new Set(selectedIds);
              if (allSel) rows.forEach((r) => next.delete(r.funding_id));
              else rows.forEach((r) => next.add(r.funding_id));
              setSelectedIds(next);
            };
            const toggleOne = (id) => {
              const next = new Set(selectedIds);
              next.has(id) ? next.delete(id) : next.add(id);
              setSelectedIds(next);
            };
            return (
              <Card className="border-yellow-200">
                <CardHeader className="py-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Clock className="w-4 h-4 text-yellow-600" /> Needs Action
                    <Badge className="bg-yellow-500">{pendingRequests.length}</Badge>
                  </CardTitle>
                  <CardDescription className="text-xs">Blockchain verification is informational only — wallet credit requires manual Admin approval.</CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  {/* Compact filter bar */}
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <div className="relative flex-1 min-w-[200px]">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                      <Input value={fSearch} onChange={(e) => setFSearch(e.target.value)} placeholder="Search Funding ID / Merchant / Email" className="pl-8 h-8 text-sm" data-testid="funding-search" />
                    </div>
                    <Select value={fStatus} onValueChange={setFStatus}>
                      <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="funding-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending_approval">Pending Approval</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={fChain} onValueChange={setFChain}>
                      <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="funding-chain-filter"><SelectValue placeholder="Verification" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Verification</SelectItem>
                        <SelectItem value="verified">Verified</SelectItem>
                        <SelectItem value="not_checked">Not Verified</SelectItem>
                        <SelectItem value="failed">Failed</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" className="h-8" onClick={fetchData} data-testid="funding-refresh-btn2"><RefreshCw className="h-3.5 w-3.5 mr-1" />Refresh</Button>
                  </div>

                  <div className="overflow-x-auto border rounded-lg">
                    <table className="w-full text-sm min-w-[920px]">
                      <thead>
                        <tr className="border-b bg-muted/40 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                          <th className="py-2 px-3 w-8"><Checkbox checked={allSel} onCheckedChange={toggleAll} data-testid="select-all-funding" /></th>
                          <th className="py-2 px-3">Funding ID</th>
                          <th className="py-2 px-3">Merchant</th>
                          <th className="py-2 px-3 text-right">USDT</th>
                          <th className="py-2 px-3 text-right">Rate</th>
                          <th className="py-2 px-3 text-right">INR Credit</th>
                          <th className="py-2 px-3">Date &amp; Time</th>
                          <th className="py-2 px-3">Status</th>
                          <th className="py-2 px-3">Verification</th>
                          <th className="py-2 px-3 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => {
                          const chain = r.chain_verification_status || r.onchain_status || 'not_checked';
                          const d = r.created_at ? new Date(r.created_at) : null;
                          return (
                            <tr key={r.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors" data-testid={`pending-${r.funding_id}`}>
                              <td className="py-2 px-3"><Checkbox checked={selectedIds.has(r.funding_id)} onCheckedChange={() => toggleOne(r.funding_id)} data-testid={`select-${r.funding_id}`} /></td>
                              <td className="py-2 px-3"><div className="font-mono font-semibold">{r.funding_id}</div><div className="text-[11px] text-muted-foreground font-mono">{r.mid || r.merchant?.mid || '—'}</div></td>
                              <td className="py-2 px-3"><div className="font-medium truncate max-w-[180px]">{r.merchant?.business_name || '-'}</div><div className="text-[11px] text-muted-foreground truncate max-w-[180px]">{r.merchant_email || r.merchant?.email || '—'}</div></td>
                              <td className="py-2 px-3 text-right font-medium whitespace-nowrap">{r.usdt_amount}<span className="text-[10px] text-muted-foreground ml-1">USDT</span></td>
                              <td className="py-2 px-3 text-right whitespace-nowrap">₹{r.exchange_rate}</td>
                              <td className="py-2 px-3 text-right font-semibold text-emerald-600 whitespace-nowrap">{formatCurrency(r.inr_amount)}</td>
                              <td className="py-2 px-3 whitespace-nowrap"><div className="text-xs">{d ? d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</div><div className="text-[11px] text-muted-foreground">{d ? d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}</div></td>
                              <td className="py-2 px-3"><Badge className="bg-yellow-100 text-yellow-700 border-none text-[10px] whitespace-nowrap" data-testid={`funding-status-${r.funding_id}`}>● Pending Approval</Badge></td>
                              <td className="py-2 px-3"><ChainBadge status={chain} /></td>
                              <td className="py-2 px-3">
                                <div className="flex items-center gap-1 justify-end">
                                  <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => { setSelectedRequest(r); setOnchain(null); setShowDetailsDialog(true); }} data-testid={`view-${r.funding_id}`}><Eye className="w-3.5 h-3.5" /></Button>
                                  <Button size="sm" className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => openApprove(r)} disabled={processing} data-testid={`approve-${r.funding_id}`}><CheckCircle className="w-3.5 h-3.5" /></Button>
                                  <Button variant="destructive" size="sm" className="h-7 px-2 text-xs" onClick={() => { setSelectedRequest(r); setRejectReason(''); setShowRejectDialog(true); }} data-testid={`reject-${r.funding_id}`}><XCircle className="w-3.5 h-3.5" /></Button>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`more-${r.funding_id}`}><MoreVertical className="w-4 h-4" /></Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-40">
                                      <DropdownMenuItem onClick={() => { setSelectedRequest(r); setOnchain(null); setShowDetailsDialog(true); }}><Eye className="w-4 h-4 mr-2" />View</DropdownMenuItem>
                                      {r.tx_hash_submitted && (
                                        <DropdownMenuItem onClick={() => handleOnchainVerify(r.funding_id)} data-testid={`verify-chain-${r.funding_id}`}><ShieldCheck className="w-4 h-4 mr-2" />Verify Chain</DropdownMenuItem>
                                      )}
                                      <DropdownMenuItem onClick={() => openApprove(r)}><CheckCircle className="w-4 h-4 mr-2" />Approve</DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => { setSelectedRequest(r); setRejectReason(''); setShowRejectDialog(true); }} className="text-red-600 focus:text-red-600"><XCircle className="w-4 h-4 mr-2" />Reject</DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {rows.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-muted-foreground text-sm">No matching funding requests</td></tr>}
                      </tbody>
                    </table>
                  </div>
                  {selectedIds.size > 0 && <p className="text-xs text-muted-foreground mt-2" data-testid="selected-count">{selectedIds.size} selected</p>}
                </CardContent>
              </Card>
            );
          })()}

          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><ArrowUpCircle className="w-5 h-5" />All Funding Requests</CardTitle></CardHeader>
            <CardContent>
              {requests.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground"><ArrowUpCircle className="w-12 h-12 mx-auto opacity-50 mb-4" /><p>No funding requests yet</p></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-sm text-muted-foreground">
                        <th className="text-left py-3 px-4">Funding ID</th>
                        <th className="text-left py-3 px-4">Merchant</th>
                        <th className="text-right py-3 px-4">USDT</th>
                        <th className="text-right py-3 px-4">Rate</th>
                        <th className="text-right py-3 px-4">INR Credit</th>
                        <th className="text-center py-3 px-4">Status</th>
                        <th className="text-left py-3 px-4">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requests.map((r) => (
                        <tr key={r.id} className="border-b last:border-0 hover:bg-muted/50 cursor-pointer" onClick={() => { setSelectedRequest(r); setShowDetailsDialog(true); }} data-testid={`row-${r.funding_id}`}>
                          <td className="py-3 px-4"><span className="font-mono text-sm">{r.funding_id}</span></td>
                          <td className="py-3 px-4"><p className="font-medium text-sm">{r.merchant?.business_name || '-'}</p></td>
                          <td className="py-3 px-4 text-right">{r.usdt_amount} USDT</td>
                          <td className="py-3 px-4 text-right">₹{r.exchange_rate}</td>
                          <td className="py-3 px-4 text-right font-semibold">{formatCurrency(r.inr_amount)}</td>
                          <td className="py-3 px-4 text-center"><StatusBadge status={r.status} /></td>
                          <td className="py-3 px-4 text-sm text-muted-foreground">{formatDate(r.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== WALLET & SETTINGS TAB ===== */}
        <TabsContent value="wallet" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Active wallet + QR */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Coins className="w-5 h-5 text-emerald-500" />Active Receiving Wallet</CardTitle>
                <CardDescription>Network: TRON — TRC20</CardDescription>
              </CardHeader>
              <CardContent>
                {activeWallet ? (
                  <div className="space-y-4" data-testid="active-wallet">
                    <div className="flex justify-center p-4 bg-white rounded-lg border">
                      <QRCodeSVG id={`qr-${activeWallet.address}`} value={activeWallet.address} size={150} level="M" includeMargin />
                    </div>
                    <div className="flex items-center gap-2">
                      <Input value={activeWallet.address} readOnly className="font-mono text-xs" />
                      <Button variant="outline" size="icon" onClick={() => copyToClipboard(activeWallet.address)}><Copy className="h-4 w-4" /></Button>
                      <Button variant="outline" size="icon" onClick={() => downloadQR(activeWallet.address)}><Download className="h-4 w-4" /></Button>
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-700 border-none">ACTIVE</Badge>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground py-8 text-center">No active receiving wallet. Add and activate one below.</p>
                )}
              </CardContent>
            </Card>

            {/* Add wallet */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Plus className="w-5 h-5" />Add USDT Receiving Wallet</CardTitle>
                <CardDescription>Only the public TRC20 address is stored. Never store private keys.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Network</Label>
                  <Input value="TRC20 (TRON)" readOnly />
                </div>
                <div className="space-y-2">
                  <Label>Wallet Address</Label>
                  <Input placeholder="T..." value={newAddress} onChange={(e) => { setNewAddress(e.target.value); setAddrValid(null); }} className="font-mono text-sm" data-testid="new-address-input" />
                  {addrValid === true && <p className="text-xs text-emerald-600">✓ Valid TRC20 address</p>}
                  {addrValid === false && <p className="text-xs text-red-600">✗ Invalid TRC20 address</p>}
                </div>
                <div className="space-y-2">
                  <Label>Label (optional)</Label>
                  <Input placeholder="e.g. Primary Treasury" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={validateAddress} data-testid="validate-address-btn">Validate Address</Button>
                  <Button onClick={() => addWallet(true)} disabled={savingWallet} data-testid="save-wallet-btn">
                    {savingWallet ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Zap className="mr-2 h-4 w-4" />}Save & Activate
                  </Button>
                  <Button variant="secondary" onClick={() => addWallet(false)} disabled={savingWallet}>Save Only</Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Wallet list */}
          <Card>
            <CardHeader><CardTitle>All Receiving Wallets</CardTitle></CardHeader>
            <CardContent>
              {wallets.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">No wallets added yet</p>
              ) : (
                <div className="space-y-3">
                  {wallets.map((w) => (
                    <div key={w.id} className="flex items-center justify-between p-3 rounded-lg border" data-testid={`wallet-${w.id}`}>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs break-all">{w.address}</span>
                          <button onClick={() => copyToClipboard(w.address)}><Copy className="h-3 w-3 text-muted-foreground" /></button>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{w.label || 'TRC20'} · added {formatDate(w.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={w.status === 'active' ? 'bg-emerald-100 text-emerald-700 border-none' : w.status === 'disabled' ? 'bg-red-100 text-red-700 border-none' : 'bg-gray-100 text-gray-600 border-none'}>{w.status}</Badge>
                        {w.status !== 'active' && w.status !== 'disabled' && (
                          <Button size="sm" variant="outline" onClick={() => activateWallet(w.id)} data-testid={`activate-${w.id}`}>Activate</Button>
                        )}
                        {w.status !== 'disabled' && (
                          <Button size="sm" variant="ghost" onClick={() => disableWallet(w.id)} data-testid={`disable-${w.id}`}><Power className="h-4 w-4" /></Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Funding method availability */}
          <Card data-testid="funding-methods-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><IndianRupee className="w-5 h-5" />Funding Methods</CardTitle>
              <CardDescription>Turn the payment methods merchants can use to add funds on or off. Merchants can never use a method disabled here.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div><p className="font-medium">Crypto Deposit (USDT / TRC20)</p><p className="text-xs text-muted-foreground">Also requires USDT Funding (below) to be enabled with an active wallet</p></div>
                <Switch checked={!!methodFlags.crypto_deposit_enabled} disabled={savingMethods} onCheckedChange={(v) => saveMethods({ ...methodFlags, crypto_deposit_enabled: v })} data-testid="method-crypto-switch" />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div><p className="font-medium">Cash Deposit</p><p className="text-xs text-muted-foreground">Allow merchants to request funding via cash deposit</p></div>
                <Switch checked={!!methodFlags.cash_deposit_enabled} disabled={savingMethods} onCheckedChange={(v) => saveMethods({ ...methodFlags, cash_deposit_enabled: v })} data-testid="method-cash-switch" />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div><p className="font-medium">Bank Transfer</p><p className="text-xs text-muted-foreground">Allow merchants to request funding via bank transfer</p></div>
                <Switch checked={!!methodFlags.bank_transfer_enabled} disabled={savingMethods} onCheckedChange={(v) => saveMethods({ ...methodFlags, bank_transfer_enabled: v })} data-testid="method-bank-switch" />
              </div>
            </CardContent>
          </Card>

          {/* Funding settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><IndianRupee className="w-5 h-5" />Funding Settings</CardTitle>
              <CardDescription>Exchange rate is locked onto each funding request at submit time (historical requests never change)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div><p className="font-medium">USDT Funding</p><p className="text-xs text-muted-foreground">Enable merchants to fund via TRC20 USDT</p></div>
                <Switch checked={settingsForm.enabled} onCheckedChange={(v) => setSettingsForm((f) => ({ ...f, enabled: v }))} data-testid="funding-enabled-switch" />
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2"><Label>Exchange Rate (₹ / USDT)</Label><Input type="number" value={settingsForm.exchange_rate} onChange={(e) => setSettingsForm((f) => ({ ...f, exchange_rate: e.target.value }))} data-testid="rate-input" /></div>
                <div className="space-y-2"><Label>Min USDT</Label><Input type="number" value={settingsForm.min_amount} onChange={(e) => setSettingsForm((f) => ({ ...f, min_amount: e.target.value }))} /></div>
                <div className="space-y-2"><Label>Max USDT</Label><Input type="number" value={settingsForm.max_amount} onChange={(e) => setSettingsForm((f) => ({ ...f, max_amount: e.target.value }))} /></div>
              </div>
              <div className="space-y-2"><Label>Instructions</Label><Textarea rows={2} value={settingsForm.instructions} onChange={(e) => setSettingsForm((f) => ({ ...f, instructions: e.target.value }))} placeholder="Send USDT only via the TRC20 network to the active address shown." /></div>
              <Button onClick={saveSettings} disabled={savingSettings} data-testid="save-settings-btn">
                {savingSettings ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Save Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Funding Request — {selectedRequest?.funding_id}</DialogTitle></DialogHeader>
          {selectedRequest && (
            <div className="space-y-4">
              <div className="flex justify-center gap-2 flex-wrap">
                <Badge className="bg-yellow-100 text-yellow-700 border-none">● {_normUi(selectedRequest.funding_status || selectedRequest.status)}</Badge>
                <ChainBadge status={selectedRequest.chain_verification_status || 'not_checked'} />
              </div>
              <div className="text-center py-4 border-y">
                <p className="text-sm text-muted-foreground">Net Wallet Credit</p>
                <p className="text-3xl font-bold text-primary">{formatCurrency(selectedRequest.net_credit || selectedRequest.inr_amount)}</p>
                <p className="text-sm text-muted-foreground mt-1">{selectedRequest.usdt_amount} USDT × ₹{selectedRequest.exchange_rate} = {formatCurrency(selectedRequest.inr_amount)}{selectedRequest.fee ? ` − ${formatCurrency(selectedRequest.fee)} fee` : ''}</p>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Merchant</span><span className="font-medium">{selectedRequest.merchant?.business_name || '-'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Network</span><span>{selectedRequest.network || 'TRC20'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Receiving Address</span><span className="font-mono text-xs">{(selectedRequest.admin_wallet_address || '').slice(0, 10)}…</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Created</span><span>{formatDate(selectedRequest.created_at)}</span></div>
                {selectedRequest.verified_at && <div className="flex justify-between"><span className="text-muted-foreground">Verified</span><span>{formatDate(selectedRequest.verified_at)}</span></div>}
              </div>
              {selectedRequest.transaction_hash && (
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground mb-1">Transaction Hash</p>
                  <div className="flex items-center gap-2"><span className="font-mono text-xs break-all">{selectedRequest.transaction_hash}</span><button onClick={() => copyToClipboard(selectedRequest.transaction_hash)}><Copy className="h-4 w-4 text-muted-foreground flex-shrink-0" /></button>
                    <a href={`https://tronscan.org/#/transaction/${(selectedRequest.transaction_hash || '').replace(/^0x/, '')}`} target="_blank" rel="noreferrer" data-testid="tron-explorer-link"><ExternalLink className="h-4 w-4 text-muted-foreground flex-shrink-0" /></a>
                  </div>
                </div>
              )}

              {/* Blockchain verification */}
              {selectedRequest.transaction_hash && !['approved', 'credited', 'rejected'].includes(selectedRequest.status) && (
                <div className="p-3 rounded-lg border space-y-2" data-testid="onchain-section">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">Blockchain Verification</p>
                    <Button size="sm" variant="outline" onClick={() => handleOnchainVerify(selectedRequest.funding_id)} disabled={verifying} data-testid="onchain-verify-btn">
                      {verifying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}Verify On-Chain
                    </Button>
                  </div>
                  {(onchain || selectedRequest.onchain_verification) && (() => {
                    const v = onchain || selectedRequest.onchain_verification;
                    const Chk = ({ ok, label }) => (<div className="flex justify-between"><span className="text-muted-foreground">{label}</span><span className={ok ? 'text-emerald-600' : 'text-red-600'}>{ok ? '✓' : '✕'}</span></div>);
                    return (
                      <div className="text-xs space-y-1" data-testid="onchain-result">
                        <div className="flex justify-between"><span className="text-muted-foreground">Transaction</span><span>{v.transaction_success ? 'SUCCESS' : (v.failure_reason || 'FAILED')}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">On-Chain Amount</span><span className="font-mono">{v.amount_usdt ?? '—'} USDT</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Expected</span><span className="font-mono">{v.expected_amount} USDT</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Sender</span><span className="font-mono">{(v.sender_address || '—').slice(0, 12)}…</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Confirmations</span><span>{v.confirmations}/{v.required_confirmations}</span></div>
                        <Chk ok={v.network_match} label="Network (TRON)" />
                        <Chk ok={v.token_match} label="Token (USDT TRC20)" />
                        <Chk ok={v.amount_match} label="Amount Match" />
                        <Chk ok={v.destination_match} label="Destination Match" />
                        <Chk ok={!v.duplicate} label="Not Duplicate" />
                        <div className={`mt-1 text-center font-medium ${v.verified ? 'text-emerald-600' : 'text-red-600'}`} data-testid="onchain-verdict">
                          {v.verified ? '✓ Transaction Verified On-Chain' : (v.failure_reason || 'Verification failed')}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {selectedRequest.rejection_reason && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200"><p className="text-sm font-medium text-red-700">Rejection Reason</p><p className="text-sm text-red-600">{selectedRequest.rejection_reason}</p></div>
              )}
              {!['credited', 'rejected', 'expired'].includes(selectedRequest.status) && (
                <div className="flex gap-3 pt-2">
                  <Button variant="destructive" className="flex-1" onClick={() => { setRejectReason(''); setShowRejectDialog(true); }} data-testid="detail-reject-btn">
                    <XCircle className="mr-2 h-4 w-4" />Reject
                  </Button>
                  <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => { setShowDetailsDialog(false); setShowApproveDialog(true); }} disabled={processing} data-testid="detail-approve-btn">
                    <CheckCircle className="mr-2 h-4 w-4" />Approve
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Approve Confirmation Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle>Approve USDT Funding?</DialogTitle>
            <DialogDescription>This will credit the merchant wallet. This action cannot be undone.</DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Merchant</span><span className="font-medium">{selectedRequest.merchant?.business_name || '-'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Funding ID</span><span className="font-mono">{selectedRequest.funding_id}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">USDT</span><span className="font-medium">{selectedRequest.usdt_amount} USDT</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Rate</span><span>₹{selectedRequest.exchange_rate}</span></div>
              <div className="flex justify-between border-t pt-2"><span className="font-semibold">Merchant Wallet Credit</span><span className="font-bold text-emerald-600" data-testid="approve-credit-amount">{formatCurrency(selectedRequest.net_credit || selectedRequest.inr_amount)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Chain Verification</span><ChainBadge status={selectedRequest.chain_verification_status || selectedRequest.onchain_status || 'not_checked'} /></div>
              {(selectedRequest.chain_verification_status || selectedRequest.onchain_status) !== 'verified' && (
                <div className="rounded-lg border border-amber-500/40 bg-amber-50 p-3 text-xs text-amber-700 mt-1" data-testid="approve-chain-warning">
                  ⚠ The blockchain transaction has not been verified. Approve only if you have manually confirmed the funds.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleApprove(selectedRequest.funding_id)} disabled={processing} data-testid="confirm-approve-btn">
              {processing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle className="mr-2 h-4 w-4" />}Confirm Approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject Funding Request</DialogTitle><DialogDescription>Provide a reason for rejecting this request.</DialogDescription></DialogHeader>
          <div className="space-y-2"><Label htmlFor="rejectReason">Rejection Reason</Label><Textarea id="rejectReason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3} /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject} disabled={processing}>{processing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <XCircle className="mr-2 h-4 w-4" />}Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
